import type { SupabaseClient } from '@supabase/supabase-js';

// ---------------------------------------------------------------------------
// Standard image sizes for imgproxy / Supabase Image Transformations
// ---------------------------------------------------------------------------

const STANDARD_SIZES = {
  thumb:  { width: 256,  quality: 75 },
  small:  { width: 512,  quality: 80 },
  medium: { width: 1024, quality: 85 },
  large:  { width: 2048, quality: 85 },
} as const;

export type ImageSize = keyof typeof STANDARD_SIZES;
export const IMAGE_SIZES = STANDARD_SIZES;

export const WANDERO_BUCKET = 'wandero';

/**
 * Private storage bucket for shopper-uploaded own-photo originals (PII).
 * `public = false`, service-role-only RLS — NO public read policy. Clients
 * never build a public object URL for this bucket; every client-facing view
 * fetches a short-lived signed-read URL minted server-side by the api
 * (`POST /v1/own-photo/read-url`). Provisioned by migration
 * `20260718170223_wandero_private_bucket.sql` (idea T11). See
 * `schemas/own-photo.ts` `OwnPhotoOriginalRef.bucket`.
 */
export const WANDERO_PRIVATE_BUCKET = 'wandero-private';

export interface ImageOpts {
  width: number;
  height?: number;
  quality?: number;
  resize?: 'cover' | 'contain' | 'fill';
}

/**
 * Build an imgproxy transform URL for a wallpaper/material/interior stored in
 * the `wandero` bucket.
 *
 * @param supabase  - Supabase client instance
 * @param originalPath - Full path in the bucket, e.g. 'wps/abc-123.jpg'
 * @param size      - Named size or explicit opts object
 */
export function wpImageUrl(
  supabase: SupabaseClient,
  originalPath: string,
  size: ImageSize | ImageOpts,
): string {
  const opts: ImageOpts =
    typeof size === 'string'
      ? { ...STANDARD_SIZES[size], resize: 'cover' }
      : { quality: 85, resize: 'cover', ...size };

  return supabase.storage
    .from(WANDERO_BUCKET)
    .getPublicUrl(originalPath, {
      transform: {
        width: opts.width,
        ...(opts.height !== undefined && { height: opts.height }),
        ...(opts.quality !== undefined && { quality: opts.quality }),
        ...(opts.resize !== undefined && { resize: opts.resize }),
      },
    }).data.publicUrl;
}

/**
 * Generate a `srcset` string for thumb / small / medium sizes.
 * Excludes 'large' to avoid loading 2048px images via srcset in most contexts.
 */
export function wpImageSrcSet(supabase: SupabaseClient, originalPath: string): string {
  return (Object.keys(STANDARD_SIZES) as ImageSize[])
    .filter((k) => k !== 'large')
    .map((size) => `${wpImageUrl(supabase, originalPath, size)} ${STANDARD_SIZES[size].width}w`)
    .join(', ');
}

// ---------------------------------------------------------------------------
// Interior image URL helper (WS5 — responsive wiring for interior images)
// ---------------------------------------------------------------------------

/**
 * Build a public URL for an interior image stored in the `wandero` bucket.
 *
 * - Without `size`: returns the **base** public URL (no imgproxy transform).
 *   This is the canonical version used in sitemap/schema — ONE logical URL per
 *   file, no `?width=` / transform query parameters (stable for SEO indexing).
 *
 * - With `size`: delegates to imgproxy via {@link wpImageUrl} — returns a
 *   transform URL for on-page responsive delivery. Used by future client-side
 *   `<picture>` / `<img srcset>` rendering (not yet live as of WS5).
 *
 * @param supabase    - Supabase client (used to resolve storage base URL)
 * @param storagePath - Path within the wandero bucket, e.g. `interiors/tulipany-sypialnia-1-6793.webp`
 * @param size        - Optional named size or explicit opts. When omitted → base URL (no transform).
 *
 * @example
 * // sitemap / schema — base URL, no transform
 * interiorImageUrl(sb, 'interiors/tulipany-sypialnia-1-6793.webp')
 * // → 'https://...supabase.co/storage/v1/object/public/wandero/interiors/tulipany-sypialnia-1-6793.webp'
 *
 * // on-page responsive delivery — imgproxy resize
 * interiorImageUrl(sb, 'interiors/tulipany-sypialnia-1-6793.webp', 'medium')
 * // → 'https://...supabase.co/storage/v1/render/image/public/wandero/interiors/...?width=1024&quality=85&resize=cover'
 */
export function interiorImageUrl(
  supabase: SupabaseClient,
  storagePath: string,
  size?: ImageSize | ImageOpts,
): string {
  if (size === undefined) {
    // Base URL — no transform (canonical, sitemap-safe, SEO-stable)
    return supabase.storage
      .from(WANDERO_BUCKET)
      .getPublicUrl(storagePath).data.publicUrl;
  }
  // Responsive delivery — imgproxy resize (same as wallpaper images)
  return wpImageUrl(supabase, storagePath, size);
}
